import {VerifiedHTMLDiv} from '@github-ui/safe-html/VerifiedHTML'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {Stack, UnderlineNav} from '@primer/react'
import Screenshots from '../../components/Screenshots'
import {CopilotListingRequirement} from '../../components/CopilotListingRequirement'
import {Plans} from './pricing-plans/Plans'
import {TransparencySection} from './TransparencySection'
import type {AppListing} from '@github-ui/marketplace-common'
import type {Screenshot, PlanInfo, PermissionsData} from '../../types'
import styles from '../../marketplace.module.css'
import {Resources} from './sidebar/Resources'
import {Languages} from './sidebar/Languages'
import {BookIcon, LogIcon} from '@primer/octicons-react'
import {ExtendedLink} from '@github-ui/react-router'
import {useSearchParams} from '@github-ui/use-navigate'
import InstallUnavailableBanner from './InstallUnavailableBanner'
import {TermsOfService} from './pricing-plans/TermsOfService'

interface BodyProps {
  app: AppListing
  screenshots: Screenshot[]
  planInfo: PlanInfo
  supportedLanguages: string[]
  permissionsData: PermissionsData[]
}

export function Body(props: BodyProps) {
  const {app, screenshots, planInfo, supportedLanguages, permissionsData} = props
  const selectedPlan = planInfo.plans.find(plan => plan.id === planInfo.selectedPlanId)

  const [searchParams] = useSearchParams()
  const tabParam = searchParams.get('tab')
  const activeTab = tabParam === 'transparency' ? 'transparency' : 'readme'

  const readmeContent = (
    <Stack gap="normal" data-testid="readme-content">
      {app.fullDescription && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
        <VerifiedHTMLDiv className="markdown-body" html={app.fullDescription as SafeHTMLString} />
      )}
      {app.extendedDescription && (
        /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
        <VerifiedHTMLDiv className="markdown-body" html={app.extendedDescription as SafeHTMLString} />
      )}
      <Screenshots screenshots={screenshots} />
    </Stack>
  )

  let content
  if (activeTab === 'transparency') {
    content = <TransparencySection app={app} permissionsData={permissionsData} />
  } else {
    content = readmeContent
  }

  return (
    <Stack gap="spacious">
      {app.copilotApp && (
        <div
          className={styles.ResponsiveVisibility}
          data-responsive-visibility="regular"
          data-testid="install-unavailable-banner-regular"
        >
          <InstallUnavailableBanner planInfo={planInfo} />
        </div>
      )}
      <div
        className={`${styles['marketplace-content-container']} ${styles['marketplace-content-container--no-padding']}`}
      >
        <UnderlineNav aria-label="Select a tab">
          <UnderlineNav.Item
            icon={BookIcon}
            as={ExtendedLink}
            to={'?tab=readme'}
            preventAutofocus
            aria-current={activeTab === 'readme' ? 'page' : undefined}
          >
            README
          </UnderlineNav.Item>
          <UnderlineNav.Item
            icon={LogIcon}
            as={ExtendedLink}
            to={'?tab=transparency'}
            preventAutofocus
            aria-current={activeTab === 'transparency' ? 'page' : undefined}
          >
            Transparency
          </UnderlineNav.Item>
        </UnderlineNav>
        <div className={styles['marketplace-content-container__content']}>{content}</div>
      </div>
      {app.copilotApp && <CopilotListingRequirement />}
      <Stack className="d-md-none">
        <Languages supportedLanguages={supportedLanguages} />
        <Resources app={app} />
      </Stack>
      {!app.copilotApp ? (
        <Plans listing={app} planInfo={planInfo} />
      ) : (
        <TermsOfService planInfo={planInfo} plan={selectedPlan} listing={app} />
      )}
    </Stack>
  )
}
